import { dutch } from "@a2/i18n/helpers";
import type { SchrijvenTopic } from "./types";

/**
 * First Schrijven course — explains how the DUO A2 Schrijven exam works.
 * Authored in English with dutch() markers for terms that must stay verbatim
 * across all locales (task names, brand names, pronoun forms, sample greetings).
 *
 * Content distilled from the inburgering.org A2 writing research at
 * docs/study/inburgering-org-a2-schrijven.md (which cross-references the
 * official DUO format via the Ad Appel Taaltrainingen sample exam).
 */
export const EXAMEN_OVERZICHT: SchrijvenTopic = {
  slug: "examen-overzicht",
  title: "How the A2 Schrijven exam works",
  tagline:
    "Four short tasks, one rubric, one golden rule — everything you need to know for exam day.",
  duration: "8 min read",
  summary:
    "Read this before your first practice exam. You will learn the four task types, the timing, the grading, and the rule that can make or break your score.",
  sections: [
    {
      kind: "paragraph",
      text: [
        "The A2 Schrijven exam is one of the four parts of the inburgering exam. You take it on a computer at a ",
        dutch("DUO"),
        " exam centre. This chapter tells you exactly what to expect — so there are no surprises on the day.",
      ],
    },

    { kind: "heading", level: 2, text: "Four kinds of tasks" },
    {
      kind: "paragraph",
      text: "The exam is made up of four short writing tasks. Each one has its own format and its own tone. Below you will see what to watch for.",
    },
    {
      kind: "table",
      headers: ["Task", "What you write", "Tone"],
      emphasizeFirstCol: true,
      rows: [
        [
          [dutch("E-mail")],
          "A short email to an institution or a person (doctor, council, school, real-estate agent).",
          ["Formal — ", dutch("u"), "-form, with a proper greeting and closing."],
        ],
        [
          [dutch("Wijkkrant")],
          "A short notice for the neighbourhood paper: an announcement, a lost item, an invitation.",
          "Public, half-formal — no greeting or closing needed.",
        ],
        [
          [dutch("Formulier")],
          "Filling in a form — personal details and a few open questions.",
          "No tone — just be factual and complete.",
        ],
        [
          [dutch("Briefje")],
          "A short note for a neighbour, colleague, family member, or partner.",
          ["Informal — ", dutch("je"), "-form for family and friends."],
        ],
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "Tone either fits or it does not",
      text: [
        "Graders pay close attention to tone. A note to your grandmother in ",
        dutch("u"),
        "-form feels strange. An email to the council in ",
        dutch("je"),
        "-form is wrong. Always read the prompt first to see who the recipient is.",
      ],
    },

    { kind: "heading", level: 2, text: "How much time do you have?" },
    {
      kind: "paragraph",
      text: "You get about 40 minutes for all four tasks together. That is roughly 10 minutes per task. A form usually takes less; an email or neighbourhood-paper notice takes a bit more. You choose the order yourself.",
    },
    {
      kind: "callout",
      tone: "rule",
      title: "Plan your time",
      text: "Start with the task you find easiest. That keeps you calm. Save 2 minutes at the end to read everything once more.",
    },

    { kind: "heading", level: 2, text: "How are you graded?" },
    {
      kind: "paragraph",
      text: "Each task is scored on five parts. Together they form your grade for that task. Below you can see what each part means.",
    },
    {
      kind: "table",
      headers: ["Criterion", "What counts", "Max"],
      emphasizeFirstCol: true,
      rows: [
        [
          [dutch("Adequaatheid")],
          "Did you cover every requested point? Does your answer address the question?",
          "3 points",
        ],
        [
          [dutch("Grammatica")],
          "Verbs, word order, articles, plurals at A2 level.",
          "2 points",
        ],
        [
          [dutch("Spelling")],
          "Word spelling, plus EXACT copying of names, numbers, and addresses.",
          "2 points",
        ],
        [
          [dutch("Begrijpelijkheid")],
          [
            "Logical sentence order, plus at least one connector word (",
            dutch("en"),
            ", ",
            dutch("maar"),
            ", ",
            dutch("want"),
            ", ",
            dutch("omdat"),
            ", ",
            dutch("dus"),
            ").",
          ],
          "1 point",
        ],
        [
          [dutch("Woordenschat")],
          "Are you using suitable A2 words? Not too simple, not too complex.",
          "2 points",
        ],
      ],
    },

    { kind: "heading", level: 2, text: "The golden rule — the auto-zero" },
    {
      kind: "callout",
      tone: "warning",
      title: "Watch out",
      text: "If you fail to answer one of the requested points at all, OR if your answer cannot be understood, you score 0 for the entire task — even with perfect grammar and spelling.",
    },
    {
      kind: "paragraph",
      text: [
        "This is called the auto-zero or catch rule. ",
        dutch("Adequaatheid"),
        " and ",
        dutch("Begrijpelijkheid"),
        " are the two gates. If you score zero on either, the other three drop to zero as well. So read the prompt calmly and make sure every point comes back in your answer.",
      ],
    },
    {
      kind: "list",
      items: [
        "Read the prompt twice.",
        "Make a short plan — which point goes where in your answer?",
        "Write clear sentences. Simple-and-correct beats complex-and-wrong.",
        [
          "Use at least one connector word (",
          dutch("omdat"),
          ", ",
          dutch("want"),
          ", ",
          dutch("dus"),
          ", ",
          dutch("maar"),
          ").",
        ],
        "Read your text through again at the end.",
      ],
    },

    { kind: "heading", level: 2, text: "Per task type — what matters?" },

    { kind: "heading", level: 3, text: [dutch("E-mail")] },
    {
      kind: "list",
      items: [
        [
          "Use ",
          dutch("u"),
          "-form (",
          dutch("u"),
          ", ",
          dutch("uw"),
          ", ",
          dutch("uzelf"),
          ").",
        ],
        [
          "Greeting: ",
          dutch("Geachte mevrouw/meneer,"),
          " or ",
          dutch("Geachte mevrouw Bakker,"),
          ".",
        ],
        [
          "Closing: ",
          dutch("Met vriendelijke groet,"),
          " followed by your name.",
        ],
        "Address every point from the prompt in its own sentence or paragraph.",
      ],
    },

    { kind: "heading", level: 3, text: [dutch("Wijkkrant")] },
    {
      kind: "list",
      items: [
        "No greeting or closing needed.",
        "Write at least three sentences.",
        "Lead with the key info (what, when, where).",
        "Say how people can get in touch if that fits the message.",
      ],
    },

    { kind: "heading", level: 3, text: [dutch("Formulier")] },
    {
      kind: "list",
      items: [
        "Fill in EVERY field that looks required. An empty required field is a missed point.",
        "Personal details (name, address, postcode, phone) — invent them if the prompt says so.",
        "Open questions at the end — write full sentences, not single words.",
      ],
    },

    { kind: "heading", level: 3, text: [dutch("Briefje")] },
    {
      kind: "list",
      items: [
        [
          "Tone depends on the recipient. Family or friends: ",
          dutch("je"),
          "-form. A teacher or landlord: ",
          dutch("u"),
          "-form.",
        ],
        [
          "Greeting and closing match that tone (",
          dutch("Hoi Marie,"),
          " or ",
          dutch("Beste juf Petra,"),
          ").",
        ],
        "Sometimes you have to invent a few things yourself (for example three presents). Count them: three means three.",
      ],
    },

    { kind: "heading", level: 2, text: "Common mistakes" },
    {
      kind: "list",
      items: [
        "Skipping one point from the prompt — instant auto-zero on that task.",
        [
          "Switching between ",
          dutch("u"),
          "-form and ",
          dutch("je"),
          "-form within the same text.",
        ],
        [
          "Using no connector word at all — ",
          dutch("Begrijpelijkheid"),
          " drops to 0.",
        ],
        [
          "Copying names or numbers wrong — costs a point on ",
          dutch("Spelling"),
          ".",
        ],
        "Writing too long and running out of time before the last task.",
      ],
    },

    {
      kind: "callout",
      tone: "tip",
      title: "Next step",
      text: 'Ready to practise? Go back to the Schrijven page and pick "Full exam" for the complete round, or "Single task" to train one task type.',
    },
  ],
};
